/* eslint-disable camelcase */
import React, { useContext } from 'react';
import { gql, useMutation, useReactiveVar } from '@apollo/client';
import { deliveryIDVar, isGlobalLoadingVar, selectedPeriodVar } from 'app/apollo/reaction';
import queryString from 'query-string';
import client from 'app/apollo/client';
import { useSetsCartMutations } from 'app/views/Main/hooks/useSetsCartMutations';
import { PathSegmentContext } from 'app/views/Main/PathSegment.context';
import { DELIVERY_BASKET_QUERY } from 'app/graphql/network/deliveryCart';

import { useParticipantIdABTest } from 'app/views/Menu/Menu/hooks/useParticipantIdABTest';
import {
    MUTATE_CART_FROM_BASKET,
    FULL_CART_FRAGMENT,
    MUTATE_CART_FROM_DELIVERY_BASKET,
} from './graphql/useCartMutationInBasket.graphql';

const readBasketDishData = (id) => {
    const cartItem = client.readFragment({
        id: `cartItem:${id}`,
        fragment: gql`
            fragment GET_FULL_CART_ITEM on cartItem {
                ...ROOT_CART_DISH_ITEM
                dish {
                ...ROOT_MENU_DISH_ITEM
                }
            }
        `,
    });
    /* @note readFragment отдаёт {} на неполном кэше (а не null),
     * приводим к null, чтобы апдейтеры ушли в guard, а не в TypeError */
    return cartItem?.dish_id ? cartItem : null;
};

const readMenuDishData = (id) => {
    const menuDish = client.readFragment({
        id: `menuDish:${id}`,
        fragment: gql`
            fragment GET_FULL_MENU_DISH on menuDish {
                id
                title
                titleEn
                caption
                captionEn
                cooking_time
                cook_until
                composition
                weight
                price
                portions
                previewImage
                previewMobileImage
                verticalImage
                umAlias
                categoryId: category_id
                category_description
                subcategory_id
                subcategory_name
                partnersComment
                parentId
                secondDishPart
                replaceableIngredient
                nutrition {
                    carbohydrate {
                        title
                        value
                        unit
                    }
                    fat {
                        title
                        value
                        unit
                    }
                    protein {
                        title
                        value
                        unit
                    }
                    calories {
                        title
                        value
                        unit
                    }
                }
                tags {
                    id
                }
            }
        `,
    });
    return menuDish?.id ? menuDish : null;
};

const readCurrentCart = (period, type = 'new') => {
    const cart = client.readFragment({
        id: `cart:${type}:${period}`,
        fragment: FULL_CART_FRAGMENT,
    });
    /* @note неполный кэш (например, корзина доп. цикла после перезагрузки)
     * отдаёт {} — без sections оптимистичный апдейтер падает, и исключение
     * в try/catch mutateItemInCart не даёт уйти мутации */
    return cart?.sections ? cart : null;
};

const swipeDishOptimisticUpdate = (dishes = [], cart) => {
    if (!dishes.length || !cart) return null;

    const dishForDelete = dishes.find((dish) => !dish.portions);
    const dishForAdd = dishes.find((dish) => !!dish.portions);
    if (!dishForDelete || !dishForAdd) return null;

    const dishForDeleteData = readBasketDishData(dishForDelete.dish_id);
    const dishForAddData = readMenuDishData(dishForAdd.dish_id);
    if (!dishForDeleteData || !dishForAddData) return null;

    const sourceSection = cart?.sections?.find(
        (section) => String(section.id) === String(dishForAddData?.categoryId),
    );
    if (!sourceSection?.items) return null;

    const nextItemsList = sourceSection.items.map((item) => {
        if (item.dish_id === dishForDeleteData.dish_id) {
            return { ...item, dish_id: dishForAddData.id, dish: dishForAddData };
        }
        return item;
    });

    const nextSections = cart.sections.map((section) => {
        if (section.id === sourceSection.id) {
            return { ...sourceSection, items: nextItemsList };
        }
        return section;
    });

    return {
        addItemsToCartByPortionsList: {
            cart: {
                ...cart,
                sections: nextSections,
            },
            success: true,
            __typename: 'addItemsToCartResult',
        },
    };
};

const changePortionOptimisticUpdate = (dishes = [], cart) => {
    if (!dishes.length || !cart) return null;
    const [dish] = dishes;
    const dishData = readBasketDishData(dish.dish_id);
    if (!dishData?.dish) return null;

    const sourceSection = cart?.sections?.find(
        (section) => String(section.id) === String(dishData?.dish?.categoryId),
    );
    if (!sourceSection?.items) return null;

    const nextItemsList = sourceSection.items.map((item) => {
        if (item.dish_id === dish.dish_id) {
            const {
                portions, price, discount_price, promoDiscountPrice,
            } = item;
            /* @note portions бывает 0/undefined на неполном кэше —
             * делим только когда есть на чём */
            const scale = (value) => (portions ? (value / portions) * dish.portions : value);
            return {
                ...item,
                portions: dish.portions,
                price: scale(price),
                discount_price: scale(discount_price),
                promoDiscountPrice: scale(promoDiscountPrice),
            };
        }
        return item;
    });

    const nextSections = cart.sections.map((section) => {
        if (section.id === sourceSection.id) {
            return { ...sourceSection, items: nextItemsList };
        }
        return section;
    });


    return {
        addItemsToCartByPortionsList: {
            cart: {
                ...cart,
                sections: nextSections,
            },
            success: true,
            __typename: 'addItemsToCartResult',
        },
    };
};

const ptimisticUpdaterRecord = {
    portion: changePortionOptimisticUpdate,
    swipe: swipeDishOptimisticUpdate,
};

const noop = () => null;

export function useCartMutationInBasket(props) {
    const { segment } = useContext(PathSegmentContext);
    const deliveryID = useReactiveVar(deliveryIDVar);
    const period = selectedPeriodVar();

    const [checkSetsParams, removeSetsParams] = useSetsCartMutations();
    const type = props?.delivery_Id ? 'delivery' : 'new';
    const OPERATION = props?.delivery_Id ? MUTATE_CART_FROM_DELIVERY_BASKET : MUTATE_CART_FROM_BASKET;

    const [mutateCart, { loading: basketLoading }] = useMutation(OPERATION, {
        context: {
            message: 'menu:mutate:useCartMutationInBasket',
        },
        refetchQueries: () => []
        // if (segment === 'delivery') {
        //     return [
        //         {
        //             query: DELIVERY_BASKET_QUERY,
        //             context: {
        //                 message: 'root:refetch:useCartMutationInBasket:Delivery',
        //             },
        //             variables: {
        //                 deliveryId: deliveryID,
        //             },
        //         },
        //     ];
        // }
        ,
        // awaitRefetchQueries: segment === 'delivery',
    });


    const mutateItemInCart = ({
        dishes,
        period: periodOverride,
        optimistic = null,
        onErrorCallback = () => { },
        onCompletedCallback = () => { },
    }) => {
        try {
            isGlobalLoadingVar(true);
            const params = queryString.parse(window.location.search);
            const periodToMutate = periodOverride || period;
            const cart = readCurrentCart(periodToMutate, type);
            const optimisticUpdate = ptimisticUpdaterRecord[optimistic] || noop;
            const subdomain = window.location.pathname?.replace(/^\/+|\/+$/g, '');
            const isSelection = 'st' in params;
            let sourceName;
            if (isSelection && subdomain.includes('basket')) {
                sourceName = 'basket';
            }

            if (isSelection && !subdomain.includes('basket')) {
                sourceName = 'collections';
            }
            return mutateCart({
                variables: {
                    delivery_id: props?.delivery_Id,
                    dishes,
                    period: periodToMutate,
                    source_name: sourceName || window.location.pathname?.replace(/^\/+|\/+$/g, ''),
                },
                optimisticResponse: optimisticUpdate(dishes, cart),
                onError: onErrorCallback,
                onCompleted: () => {
                    const isSetCart = checkSetsParams();
                    if (isSetCart) {
                        removeSetsParams();
                    }
                    onCompletedCallback();
                },
            }).finally(() => { isGlobalLoadingVar(false); });
        } catch (err) {
            console.error(err);
            isGlobalLoadingVar(false);
            return null;
        }
    };

    const isCartUpdating = basketLoading;

    return { isCartUpdating, mutateItemInCart };
}
